import { useQuery } from "@tanstack/react-query";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import api from "@/lib/api";

export const CampaignStats = ({ orderId }: { orderId: string }) => {
  const { data } = useQuery({
    queryKey: ["ad-stats", orderId],
    queryFn: async () => (await api.get(`/ad-orders/${orderId}/stats`)).data,
  });
  if (!data) return <p className="text-gray-500 text-sm py-4 font-mono">Loading stats…</p>;
  const isAudio = data.placement === "audio_brief";
  const stats = [
    { label: isAudio ? "Plays" : "Impressions", value: (data.impressions || 0).toLocaleString() },
    { label: isAudio ? "Credit clicks" : "Clicks", value: (data.clicks || 0).toLocaleString() },
    { label: "CTR", value: `${data.ctr}%` },
    { label: "Days left", value: data.days_remaining ?? "—" },
  ];
  return (
    <div data-testid={`campaign-stats-${orderId}`} className="mt-3 bg-[#0d1117] border border-white/10 rounded-lg p-4">
      <div className="flex flex-wrap items-center gap-3">
        {stats.map((s) => (
          <div key={s.label} className="bg-[#121212] border border-white/10 rounded-md px-4 py-2.5 min-w-[105px]">
            <p className="font-mono text-[9px] uppercase tracking-widest text-gray-500">{s.label}</p>
            <p className="font-mono text-lg text-white font-bold mt-0.5">{s.value}</p>
          </div>
        ))}
        {data.exclusive && (
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#F59E0B] border-l-2 border-[#F59E0B] pl-2 py-1">
            24/7 Exclusive
          </span>
        )}
      </div>
      {(data.daily || []).length > 0 && (
        <div className="h-44 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.daily} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
              <defs>
                <linearGradient id="impGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 9, fontFamily: "JetBrains Mono" }}
                axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#6b7280", fontSize: 9, fontFamily: "JetBrains Mono" }}
                axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 8, fontSize: 11, fontFamily: "JetBrains Mono", color: "#fff" }} />
              <Area type="monotone" dataKey="impressions" stroke="#00E5FF" strokeWidth={2} fill="url(#impGrad)" />
              <Area type="monotone" dataKey="clicks" stroke="#F59E0B" strokeWidth={2} fill="transparent" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      {!data.live && !data.impressions ? (
        <p className="text-gray-500 text-xs mt-3 font-mono">Campaign not live yet — stats appear once your ad is approved.</p>
      ) : null}
    </div>
  );
};
